import { createPinia, setActivePinia } from 'pinia';
import { createApp } from 'vue';

import App from './App.vue';
import { toastPlugin } from './plugins/toast';
import router from './router';
import { captureAttribution } from '@/composables/useAnalytics';


/* Service Worker */
import './registerServiceWorker';

// Global Components
import FormCard from '@/components/ui/layout/FormCard.vue';
// import Datepicker from 'vue3-datepicker'
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import { initSentry } from '@/plugins/sentry';
import './styles/css/datepicker-dark.css';
import './styles/css/main.css';
import './styles/css/theme.css';
import './styles/css/portal-demo.css';
import './styles/css/benchmark-wizard.css';
import i18n from './i18n';

import { setupRouterGuards } from './router/guards';
import { useDisplayStore } from '@/stores/useDisplayStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { installGlobalChunkLoadRecovery, isChunkLoadError } from '@/utils/chunkLoadRecovery';

// Before the router rewrites the URL: the landing URL holds the campaign/share params.
captureAttribution();

// Create Pinia before the app and activate it immediately so module-level
// `useXStore()` calls below (responsive layout init, theme setup) work
// without waiting for `app.use(pinia)` later in bootstrap.
const pinia = createPinia();
setActivePinia(pinia);

const displayStore = useDisplayStore();
const authStore = useAuthStore();
const isE2EMode = process.env.VUE_APP_E2E === 'true';
const isPortalCapture = process.env.NODE_ENV === 'development' && process.env.VUE_APP_PORTAL_CAPTURE === 'true';

if (isE2EMode && typeof window !== 'undefined') {
  document.documentElement.dataset.e2e = 'true';
  window.__PF_E2E_READY__ = { bootstrap: false, app: false, authenticated: false };

  const style = document.createElement('style');
  style.setAttribute('data-profiteia-e2e', 'true');
  style.textContent = `
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-delay: 0s !important;
      transition-duration: 0.001ms !important;
      transition-delay: 0s !important;
      scroll-behavior: auto !important;
    }
  `;
  document.head.appendChild(style);
}

// Kick off Highcharts download immediately — all static imports are above so
// ESLint is happy, and the browser starts fetching the chunk right away in
// parallel with the rest of bootstrap.
const highchartsReady = import(/* webpackChunkName: "highcharts-init" */ './plugins/highcharts');

const getTranslations = (key) => keys[key].map((item) => i18n.global.t(item));

const buildHighchartsLocaleOptions = () => ({
  lang: {
    decimalPoint: ',',
    thousandsSep: '.',
    months: getTranslations('months'),
    shortMonths: getTranslations('shortMonths'),
    weekdays: getTranslations('weekdays'),
  },
  accessibility: {
    enabled: false,
  },
});

const keys = {
  months: [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ],
  shortMonths: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
  weekdays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
};

/* Responsive layout control */
displayStore.responsiveLayoutControl();
window.onresize = () => displayStore.responsiveLayoutControl();

/* Fetch sample data */
// data.js was removed; this dead reference cleaned up

/* Uncomment to make dark default */
// displayStore.darkMode()
displayStore.defaultLocale();
displayStore.defaultTheme();

// Sync the HTML lang attribute with the active locale
document.documentElement.lang = i18n.global.locale.value;

/* Suppress harmless ResizeObserver errors in development */
const resizeObserverLoopErrRe = /ResizeObserver loop (limit exceeded|completed)/;
window.addEventListener('error', (e) => {
  if (resizeObserverLoopErrRe.test(e.message)) {
    e.stopImmediatePropagation();
  }
});

setupRouterGuards({ router, authStore, displayStore });
installGlobalChunkLoadRecovery();

// Bootstrap the app asynchronously — Highcharts is an async chunk that started
// downloading at the top of this file, so by the time all the router guards
// and store setup above have run, it has usually already arrived.
(async () => {
  // Await the Highcharts chunk — it started downloading at the top of this
  // file, so in most cases it has already arrived by this point.
  // If loading fails (e.g. stale service-worker cache referencing a missing
  // chunk), fall back to a stub so the app still mounts instead of hanging
  // forever on the loading screen.
  let Highcharts = null;
  let HighchartsVue = null;

  try {
    ({ Highcharts, HighchartsVue } = await highchartsReady);
    Highcharts.setOptions(buildHighchartsLocaleOptions());
  } catch (err) {
    console.error('[bootstrap] Highcharts failed to load:', err);

    // If this looks like a chunk-load failure, wipe the SW cache once and
    // reload so the browser fetches the current assets from the network.
    const isChunkError =
      err?.name === 'ChunkLoadError' || (err?.message && /loading chunk/i.test(err.message));

    const CHUNK_RELOAD_GUARD_KEY = 'chunkReloadAttempt';
    if (isChunkError && !sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
      console.warn('[bootstrap] Chunk load failure detected — clearing caches and reloading.');
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }
      } catch (cacheError) {
        // Cache clearing is best-effort; proceed to reload regardless.
        console.warn('[bootstrap] Cache clearing failed:', cacheError);
      }
      window.location.reload();
      return;
    }

    // Plain network error or repeated failure — create a minimal stub so Vue
    // mounts and the rest of the app (including public pages) still works.
    // Chart components will fail to render but won't block navigation.
    const { defineComponent, h } = await import('vue');
    HighchartsVue = {
      install(appInstance) {
        appInstance.component(
          'highcharts',
          defineComponent({
            render: () =>
              h('div', {
                class: 'highcharts-unavailable',
                role: 'img',
                'aria-label': 'Chart unavailable',
              }),
          })
        );
      },
    };
  }

  const appBuilder = createApp(App)
    .use(i18n)
    .use(pinia)
    .use(router)
    .use(toastPlugin);

  if (HighchartsVue) {
    if (Highcharts) {
      appBuilder.use(HighchartsVue, { highcharts: Highcharts });
    } else {
      appBuilder.use(HighchartsVue);
    }
  }

  const app = appBuilder;

  // Global error handlers for debugging
  app.config.errorHandler = (err, instance, info) => {
    console.error('[Vue errorHandler]', info, err);
  };

  window.addEventListener('error', (e) => {
    console.error('[window error]', e.error || e.message, e);
  });

  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError(e.reason)) {
      return;
    }
    console.error('[unhandledrejection]', e.reason, e);
  });

  if (!isE2EMode && !isPortalCapture) {
    initSentry({ app, router });
  }

  app.component('FormCard', FormCard);
  // app.component('Datepicker', Datepicker)
  app.component('VueDatePicker', VueDatePicker);

  app.mount('#app');

  if (isE2EMode && typeof window !== 'undefined') {
    window.__PF_E2E_READY__ = {
      ...(window.__PF_E2E_READY__ || {}),
      bootstrap: true,
      authenticated: authStore.isAuthenticated,
    };
  }
})();
